import React, { useEffect, useState } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Users, TrendingUp, Wallet, AlertCircle, Calendar, Clock } from 'lucide-react';
import { formatCurrency, formatDate } from '@/utils/helpers';
import { toast } from 'sonner';

function ManagerDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [todayCollections, setTodayCollections] = useState([]);
  const [upcomingDues, setUpcomingDues] = useState([]);
  const [activityLoading, setActivityLoading] = useState(true);

  useEffect(() => {
    fetchStats();
    fetchDashboardActivity();
  }, []);

  const fetchStats = async () => {
    try {
      const params = user.mandali_id ? { mandali_id: user.mandali_id } : {};
      const data = await api.dashboard.getStats(params);
      setStats(data);
    } catch (error) {
      toast.error('Failed to load dashboard stats');
    } finally {
      setLoading(false);
    }
  };

  // OPTIMIZED: Single API call for today's collections and overdues
  const fetchDashboardActivity = async () => {
    try {
      setActivityLoading(true);
      const params = user.mandali_id ? { mandali_id: user.mandali_id } : {};
      const data = await api.dashboard.getActivity(params);

      // Transform today_collections to expected format
      const todayPayments = data.today_collections.map(item => ({
        loan_account: item.loan_account,
        account_holder: item.account_holder,
        amount: item.amount,
        emi_no: item.emi_no,
        payment_type: item.payment_type
      }));
      setTodayCollections(todayPayments);

      // Transform overdue_emis to expected format
      const overdueList = data.overdue_emis.map(item => ({
        loan_account: item.loan_account,
        account_holder: item.account_holder,
        due_date: item.due_date,
        amount: item.amount,
        emi_no: item.emi_no,
        days_overdue: item.days_overdue
      }));
      setUpcomingDues(overdueList);
    } catch (error) {
      console.error('Failed to fetch dashboard activity', error);
    } finally {
      setActivityLoading(false);
    }
  };

  const statCards = [
    {
      title: "💰 Cash on Hand",
      subtitle: "(As of Today)",
      value: formatCurrency(stats?.cash_on_hand || 0),
      secondaryInfo: `Opening: ${formatCurrency(stats?.opening_cash_balance || 0)}`,
      icon: Wallet,
      color: 'bg-emerald-100 text-emerald-600',
      testId: 'stat-cash-on-hand',
      highlight: true,
      size: 'large',
    },
    {
      title: "Today's Collections",
      value: formatCurrency(stats?.today_collection || 0),
      breakdown: `EMI: ${formatCurrency(stats?.today_emi_collection || 0)} | Lavajam: ${formatCurrency(stats?.today_lavajam_collection || 0)}`,
      icon: Clock,
      color: 'bg-cyan-100 text-cyan-600',
      testId: 'stat-today-collection',
      highlight: true,
    },
    {
      title: "This Month's Collections",
      value: formatCurrency(stats?.current_month_collection || 0),
      breakdown: `EMI: ${formatCurrency(stats?.current_month_emi_collection || 0)} | Lavajam: ${formatCurrency(stats?.current_month_lavajam_collection || 0)}`,
      icon: Calendar,
      color: 'bg-blue-100 text-blue-600',
      testId: 'stat-month-collection',
      highlight: true,
    },
    {
      title: "🏦 Lavajam Received",
      subtitle: "(This Month)",
      value: formatCurrency(stats?.current_month_lavajam_collection || 0),
      secondaryInfo: `${stats?.total_members || 0} deposits`,
      icon: Wallet,
      color: 'bg-green-100 text-green-600',
      testId: 'stat-lavajam-month',
    },
    {
      title: "💳 EMI Received",
      subtitle: "(This Month)",
      value: formatCurrency(stats?.current_month_emi_collection || 0),
      secondaryInfo: `${stats?.due_this_month_count || 0} payments`,
      icon: TrendingUp,
      color: 'bg-purple-100 text-purple-600',
      testId: 'stat-emi-month',
    },
    {
      title: "💼 Loans Given",
      subtitle: "(This Month)",
      value: formatCurrency(stats?.loans_disbursed_month || 0),
      secondaryInfo: `Total: ${formatCurrency(stats?.total_disbursed || 0)}`,
      icon: Wallet,
      color: 'bg-teal-100 text-teal-600',
      testId: 'stat-loans-disbursed',
    },
    {
      title: 'Total Members',
      value: stats?.total_members || 0,
      icon: Users,
      color: 'bg-emerald-100 text-emerald-600',
      testId: 'stat-total-members',
    },
    {
      title: 'Active Loans',
      value: stats?.active_loans || 0,
      icon: TrendingUp,
      color: 'bg-green-100 text-green-600',
      testId: 'stat-active-loans',
    },
    {
      title: 'Pending Collections',
      value: formatCurrency(stats?.pending_collections || 0),
      icon: AlertCircle,
      color: 'bg-orange-100 text-orange-600',
      testId: 'stat-pending-collections',
    },
    {
      title: 'Savings Balance',
      subtitle: "(Total Lavajam)",
      value: formatCurrency(stats?.total_lavajam_balance || 0),
      secondaryInfo: `${stats?.total_members || 0} members`,
      icon: Wallet,
      color: 'bg-teal-100 text-teal-600',
      testId: 'stat-savings-balance',
    },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6 fade-in pt-2">
        {/* Page Header - Consistent spacing */}
        <div className="pb-2">
          <h1 className="text-2xl md:text-3xl font-bold mb-1" style={{ fontFamily: 'Crimson Text, serif' }}>
            JD Group Dashboard
          </h1>
          <p className="text-sm text-gray-600">Manage your JD Group operations</p>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Card key={i} className="animate-pulse">
                <CardHeader className="pb-2">
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                </CardHeader>
                <CardContent>
                  <div className="h-8 bg-gray-200 rounded w-3/4 mt-2"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {statCards.map((card) => {
              const Icon = card.icon;
              return (
                <Card
                  key={card.title}
                  className={`glass card-hover ${card.highlight ? 'ring-2 ring-emerald-200' : ''} ${card.size === 'large' ? 'md:col-span-2' : ''}`}
                  data-testid={card.testId}
                >
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <div className="flex-1 min-w-0">
                      <CardTitle className={`text-sm font-medium truncate ${card.highlight ? 'text-emerald-700 font-semibold' : 'text-gray-600'}`}>
                        {card.title}
                      </CardTitle>
                      {card.subtitle && (
                        <p className="text-xs text-gray-500 mt-0.5 truncate">{card.subtitle}</p>
                      )}
                    </div>
                    <div className={`p-2 rounded-lg ${card.color}`}>
                      <Icon size={20} />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className={`text-2xl font-bold truncate ${card.highlight ? 'text-emerald-600' : ''}`}>
                      {card.value}
                    </div>
                    {card.breakdown && (
                      <p className="text-xs text-gray-600 mt-2 truncate">{card.breakdown}</p>
                    )}
                    {card.secondaryInfo && (
                      <p className="text-xs text-gray-500 mt-1 truncate">{card.secondaryInfo}</p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Action Required Section */}
        {!loading && (stats?.overdue_loan_count > 0 || stats?.due_this_month_count > 0) && (
          <Card className="glass border-orange-200 bg-orange-50/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-orange-700">
                <AlertCircle size={20} />
                ⚠️ Action Required
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {stats?.overdue_loan_count > 0 && (
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-3 bg-white rounded-lg border border-orange-200 gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-gray-900 truncate">
                        {stats.overdue_loan_count} member{stats.overdue_loan_count !== 1 ? 's' : ''} overdue
                      </p>
                      <p className="text-sm text-gray-600">Last due: This month</p>
                    </div>
                    <a
                      href="/collections"
                      className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-sm font-medium whitespace-nowrap"
                    >
                      View Details →
                    </a>
                  </div>
                )}
                {stats?.due_this_month_count > 0 && (
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-3 bg-white rounded-lg border border-blue-200 gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-gray-900 truncate">
                        {stats.due_this_month_count} member{stats.due_this_month_count !== 1 ? 's' : ''} due on 20th {new Date().toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                      </p>
                      <p className="text-sm text-gray-600">Upcoming collections</p>
                    </div>
                    <a
                      href="/collections"
                      className="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors text-sm font-medium whitespace-nowrap"
                    >
                      View Collections →
                    </a>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="glass">
            <CardHeader>
              <CardTitle>Today's Collections</CardTitle>
            </CardHeader>
            <CardContent>
              {activityLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500 mx-auto"></div>
                  <p className="text-sm text-gray-500 mt-2">Loading...</p>
                </div>
              ) : todayCollections.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500 font-medium">No collections today</p>
                  <p className="text-sm text-gray-400 mt-1">Collections will appear here once payments are recorded</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex justify-between items-center pb-2 border-b">
                    <span className="font-semibold text-emerald-600">
                      Total: {formatCurrency(todayCollections.reduce((sum, p) => sum + p.amount, 0))}
                    </span>
                    <span className="text-sm text-gray-500">
                      {todayCollections.length} payment{todayCollections.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {todayCollections.map((payment, idx) => (
                      <div key={idx} className={`flex justify-between items-center p-3 rounded-lg ${payment.payment_type === 'Lavajam' ? 'bg-green-50' : 'bg-emerald-50'}`}>
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center shadow-sm text-white font-bold text-sm ${payment.payment_type === 'Lavajam' ? 'bg-gradient-to-br from-green-500 to-lime-500' : 'bg-gradient-to-br from-emerald-500 to-teal-500'}`}>
                            {payment.account_holder ? payment.account_holder.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() : 'JD'}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-sm truncate" title={payment.account_holder}>{payment.account_holder}</p>
                            <p className="text-xs text-gray-600 truncate" title={`${payment.loan_account} - ${payment.payment_type === 'Lavajam' ? 'Lavajam' : `EMI #${payment.emi_no}`}`}>
                              {payment.loan_account} - {payment.payment_type === 'Lavajam' ? '💰 Lavajam' : `EMI #${payment.emi_no}`}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className={`font-semibold ${payment.payment_type === 'Lavajam' ? 'text-green-600' : 'text-emerald-600'}`}>{formatCurrency(payment.amount)}</span>
                          {payment.payment_type === 'Lavajam' && (
                            <span className="ml-2 text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">Savings</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="glass">
            <CardHeader>
              <CardTitle>This Month Total Overdues</CardTitle>
            </CardHeader>
            <CardContent>
              {activityLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500 mx-auto"></div>
                  <p className="text-sm text-gray-500 mt-2">Loading...</p>
                </div>
              ) : upcomingDues.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500 font-medium">No overdue EMIs this month</p>
                  <p className="text-sm text-gray-400 mt-1">All EMIs are on track!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex justify-between items-center pb-2 border-b">
                    <span className="font-semibold text-red-600">
                      Total: {formatCurrency(upcomingDues.reduce((sum, d) => sum + d.amount, 0))}
                    </span>
                    <span className="text-sm text-gray-500">
                      {upcomingDues.length} overdue EMI{upcomingDues.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {upcomingDues.slice(0, 5).map((due, idx) => (
                      <div key={idx} className="flex justify-between items-center p-3 bg-red-50 rounded-lg border border-red-100">
                        <div className="flex-1 flex items-start gap-3">
                          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center shadow-sm text-white font-bold text-sm shrink-0">
                            {due.account_holder ? due.account_holder.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() : 'JD'}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-sm truncate" title={due.account_holder}>{due.account_holder}</p>
                            <p className="text-xs text-gray-600 truncate" title={`${due.loan_account} - EMI #${due.emi_no}`}>{due.loan_account} - EMI #{due.emi_no}</p>
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              <p className="text-xs text-red-600">Due: {formatDate(due.due_date)}</p>
                              <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-semibold">
                                {due.days_overdue} day{due.days_overdue !== 1 ? 's' : ''} overdue
                              </span>
                            </div>
                          </div>
                        </div>
                        <span className="font-semibold text-red-600 ml-2">{formatCurrency(due.amount)}</span>
                      </div>
                    ))}
                    {upcomingDues.length > 5 && (
                      <p className="text-center text-sm text-gray-500 pt-2">
                        +{upcomingDues.length - 5} more overdue
                      </p>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}

export default ManagerDashboard;
